import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { urgentCaseAPI } from "../../services/api";
import { createPortal } from 'react-dom';
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import { useAuth } from "../../hooks/useAuth";

const EmergencyBroadcast = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { userId, case_id } = useParams();
  const { getUserType } = useAuth();
  const userType = getUserType();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [caseData, setCaseData] = useState(null);
  const [isLoadingCaseData, setIsLoadingCaseData] = useState(false);
  const [caseStatus, setCaseStatus] = useState(null);
  const [isCaseAccepted, setIsCaseAccepted] = useState(false);
  const [isCaseFinished, setIsCaseFinished] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const [isApiResponded, setIsApiResponded] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [showEndCaseModal, setShowEndCaseModal] = useState(false);
  const navigate = useNavigate();

  // Get the correct dashboard URL based on user type
  const getDashboardUrl = () => {
    return userType === 'SERVICE_PROVIDER'
      ? '/medical/dashboard/petshopowner'
      : '/medical/dashboard/petguardian';
  };

  // Get the correct urgent case submit URL based on user type
  const getUrgentCaseSubmitUrl = (userId) => {
    return userType === 'SERVICE_PROVIDER'
      ? `/medical/dashboard/petshopowner/urgent-case-submit${userId ? `/${userId}` : ''}`
      : `/medical/dashboard/petguardian/urgent-case-submit${userId ? `/${userId}` : ''}`;
  };

  // Function to extend case expiration
  const handleExtendExpiration = async () => {
    if (!case_id) {
      console.error('No case ID available for extension');
      return;
    }

    // Check if there are already 15 minutes or more remaining
    const remainingMinutes = Math.floor(secondsLeft / 60);
    if (remainingMinutes >= 15) {
      showErrorToast(t("emergency.broadcast.extensionLimit"));
      return;
    }

    setIsExtending(true);
    try {
      const response = await urgentCaseAPI.extendCaseExpiration(case_id);

      if (response.data && response.data.status === 'success') {

        // Update the remaining time if provided in response
        if (response.data.data && response.data.data.case && response.data.data.case.remaining_time_in_seconds) {
          const newRemainingTime = Math.max(0, Math.floor(response.data.data.case.remaining_time_in_seconds));
          setSecondsLeft(newRemainingTime);
        }

        // Reset timer expired state if it was expired
        if (isTimerExpired) {
          setIsTimerExpired(false);
        }

        // Show success message with extension details
        const extensionHours = response.data.data.extension_details?.extension_hours || 1;
        const newExpireAt = response.data.data.extension_details?.new_expire_at;
        
        let message = t("emergency.broadcast.extensionSuccess", { count: extensionHours });

        if (newExpireAt) {
          const expireDate = new Date(newExpireAt);
          const timeString = expireDate.toLocaleString(i18n.language);
          message += `\n${t("emergency.broadcast.extensionNewTime", { time: timeString })}`;
        }

        alert(message);
      } else {
        console.error('Failed to extend case expiration:', response.data?.message);
        showErrorToast(t("emergency.broadcast.extensionFailed"));
      }
    } catch (error) {
      console.error('Error extending case expiration:', error);
      showErrorToast(t("emergency.broadcast.extensionError"));
    } finally {
      setIsExtending(false);
    }
  };
  // Function to show end case confirmation modal
  const handleEndCase = () => {
    if (!case_id) {
      console.error('No case ID available for ending case');
      return;
    }
    setShowEndCaseModal(true);
  };

  // Function to confirm ending the case
  const confirmEndCase = async () => {
    setIsEnding(true);
    try {
      const response = await urgentCaseAPI.endCase(case_id);

      if (response.data && response.data.status === 'success') {
        // Close modal and navigate back to dashboard or case submission page
        showSuccessToast(t("emergency.broadcast.endSuccess"));
        setShowEndCaseModal(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error ending case:', error);
      showErrorToast(t("emergency.broadcast.endError"));
    } finally {
      setIsEnding(false);
    }
  };

  // Function to cancel ending the case
  const cancelEndCase = () => {
    setShowEndCaseModal(false);
  };

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showEndCaseModal && !isEnding) {
        cancelEndCase();
      }
    };

    if (showEndCaseModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showEndCaseModal, isEnding]);

  // Function to fetch urgent case data
  const fetchCaseData = async (caseId) => {
    if (!caseId) return;

    setIsLoadingCaseData(true);
    try {
      const response = await urgentCaseAPI.getUrgentCase(caseId);

      if (response.data && response.data.status === 'success') {
        const caseData = response.data.data;
        setCaseData(caseData);
        setCaseStatus(caseData.status || 'pending');

        // Set timer based on remaining_time_in_seconds from server response (actually returns seconds)
        if (caseData.remaining_time_in_seconds !== undefined && caseData.remaining_time_in_seconds !== null) {
          const remainingTimeInSeconds = Math.max(0, Math.floor(caseData.remaining_time_in_seconds));
          setSecondsLeft(remainingTimeInSeconds);
        } else {
          // Fallback to 3600 seconds (1 hour) if no remaining time provided
          const oneHourInSeconds = 3600;
          setSecondsLeft(oneHourInSeconds);
        }

        // Mark API as responded to start timer
        setIsApiResponded(true);

        // Check if case is accepted, finished, or closed
        if (caseData.status === 'accepted') {
          setIsCaseAccepted(true);
        } else if (caseData.status === 'finished') {
          setIsCaseFinished(true);
        } else if (caseData.status === 'closed') {
          setIsCaseFinished(true); // Treat closed as finished for UI purposes
        }
      } else {
        console.error('❌ Unexpected response format:', response.data);
        setCaseData(null);
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setIsUnauthorized(true);
        setErrorMessage(error.response.data.message || 'Access denied');
        console.error('❌ Authorization error:', error.response.data.message);
      } else {
        console.error('❌ Error fetching case data:', error);
        setCaseData(null);
      }
    } finally {
      setIsLoadingCaseData(false);
    }
  };

  // Fetch case data on component mount
  useEffect(() => {
    if (case_id) {
      fetchCaseData(case_id);
    }
  }, [case_id]);

  // Handle timer expiration when component loads with already expired timer
  useEffect(() => {
    if (secondsLeft === 0 && !isCaseAccepted && !isCaseFinished && !isLoadingCaseData && isApiResponded) {
      setIsTimerExpired(true);
    }
  }, [secondsLeft, isCaseAccepted, isCaseFinished, isLoadingCaseData, isApiResponded]);

  // Timer logic
  useEffect(() => {
    // Only start timer after API responds and timer is not already expired
    if (!isApiResponded || secondsLeft <= 0 || isCaseAccepted || isCaseFinished) {
      if (secondsLeft <= 0 && !isCaseAccepted && !isCaseFinished && isApiResponded) {
        setIsTimerExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        const newTime = prev > 0 ? prev - 1 : 0;
        if (newTime === 0) {
          setIsTimerExpired(true);
        }
        return newTime;
      });
    }, 1000); // Update every second (1000ms)

    return () => clearInterval(timer);
  }, [secondsLeft, isCaseAccepted, isCaseFinished, isApiResponded]);

  // Format time to display minutes and seconds
  const formatTime = (secs) => {
    const min = String(Math.floor(secs / 60)).padStart(2, "0");
    const sec = String(secs % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };

  return (
    <div className="guardian-dash-root">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes spinnerRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        .emergency-loading-spinner {
          width: 56px;
          height: 56px;
          position: relative;
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-top-color: var(--user-type-color, #8548A8);
          border-radius: 50%;
          animation: spinnerRotate 1s linear infinite;
          margin-bottom: 20px;
        }
        .emergency-loading-spinner::before {
          content: '';
          position: absolute;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 3px solid transparent;
          border-top-color: var(--user-type-color, #8548A8);
          border-right-color: var(--user-type-color, #8548A8);
          animation: spinnerRotate 1s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        }
      `}</style>
      <div className="emergency-broadcast-wrapper">
        <div className="emergency-broadcast-container">
          {isLoadingCaseData ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              textAlign: 'center'
            }}>
              <div className="emergency-loading-spinner"></div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '10px'
              }}>
                {t("emergency.loading")}
              </h2>
              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                marginBottom: '20px'
              }}>
                {t("emergency.fetchingInfo")}
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#9CA3AF'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#3B82F6',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#3B82F6',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite 0.2s'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#3B82F6',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite 0.4s'
                }}></div>
              </div>
            </div>
          ) : (
            <>
              <div className="emergency-broadcast-header">
                {isUnauthorized ? (
                  <>
                    <h1 className="emergency-broadcast-title" style={{ color: '#DC2626' }}>
                      {t("emergency.accessDenied")}
                    </h1>
                    <p className="emergency-broadcast-desc" style={{ color: '#B91C1C' }}>
                      {errorMessage || t("emergency.notAuthorized")}
                    </p>
                    <div style={{
                      padding: '12px 20px',
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #DC2626',
                      borderRadius: '8px',
                      color: '#991B1B',
                      fontSize: '16px',
                      fontWeight: '500',
                      margin: '10px 0'
                    }}>
                      {t("emergency.unauthorizedDesc")}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '10px',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      marginTop: '20px'
                    }}>
                      <button
                        onClick={() => navigate(getDashboardUrl())}
                        style={{
                          backgroundColor: '#6B7280',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        {t("emergency.backToDashboard")}
                      </button>
                    </div>
                  </>
                ) : isCaseFinished ? (
                  <>
                    <h1 className="emergency-broadcast-title" style={{ color: '#059669' }}>
                      {caseData?.status === 'closed' ? t("emergency.caseClosed") : t("emergency.caseCompleted")}
                    </h1>
                    <p className="emergency-broadcast-desc" style={{ color: '#047857' }}>
                      {caseData?.status === 'closed'
                        ? t("emergency.sosClosed")
                        : t("emergency.sosCompletedBy", {
                          name: caseData?.doctor?.display_name ||
                            caseData?.doctor?.full_name ||
                            (caseData?.doctor?.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                            caseData?.doctor?.name ||
                            caseData?.doctor?.username ||
                            t("emergency.veterinarian")
                        })
                      }
                    </p>
                    <div style={{
                      padding: '12px 20px',
                      backgroundColor: caseData?.status === 'closed' ? '#FEF3C7' : '#D1FAE5',
                      border: `1px solid ${caseData?.status === 'closed' ? '#F59E0B' : '#10B981'}`,
                      borderRadius: '8px',
                      color: caseData?.status === 'closed' ? '#92400E' : '#065F46',
                      fontSize: '16px',
                      fontWeight: '500',
                      margin: '10px 0'
                    }}>
                      {caseData?.status === 'closed'
                        ? t("emergency.caseClosedBanner")
                        : t("emergency.treatmentCompleted")
                      }
                    </div>
                  </>
                ) : isCaseAccepted ? (
                  <>
                    <h1 className="emergency-broadcast-title" style={{ color: '#10B981' }}>
                      {t("emergency.caseAccepted")}
                    </h1>
                    <p className="emergency-broadcast-desc" style={{ color: '#059669' }}>
                      {t("emergency.caseAcceptedBy", {
                        name: caseData?.doctor?.display_name ||
                          caseData?.doctor?.full_name ||
                          (caseData?.doctor?.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                          caseData?.doctor?.name ||
                          caseData?.doctor?.username ||
                          t("emergency.veterinarian")
                      })}
                    </p>
                    <div style={{
                      padding: '12px 20px',
                      backgroundColor: '#D1FAE5',
                      border: '1px solid #10B981',
                      borderRadius: '8px',
                      color: '#065F46',
                      fontSize: '16px',
                      fontWeight: '500',
                      margin: '10px 0'
                    }}>
                      {t("emergency.veterinarianHandling")}
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="emergency-broadcast-title">
                      {t("emergency.broadcasting")}
                    </h1>
                    <p className="emergency-broadcast-desc">
                      {t("emergency.requestSending")}
                    </p>
                    <div>
                      {t("emergency.timeRemaining")}
                    </div>
                    <div className="emergency-broadcast-timer" style={{
                      color: isTimerExpired ? '#DC2626' : secondsLeft < 300 ? '#F59E0B' : 'green',
                      fontWeight: isTimerExpired ? 'bold' : 'bold'
                    }}>
                      {isTimerExpired ? t("emergency.timeExpired") : formatTime(secondsLeft)}
                    </div>
                    {isTimerExpired && (
                      <div style={{
                        padding: '12px 20px',
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #DC2626',
                        borderRadius: '8px',
                        color: '#991B1B',
                        fontSize: '16px',
                        fontWeight: '500',
                        margin: '10px 0',
                        textAlign: 'center'
                      }}>
                        {t("emergency.timeExpiredDesc")}
                      </div>
                    )}
                    <div id="live-broadcast-status" style={{
                      padding: '8px 16px',
                      backgroundColor: 'rgb(25, 92, 53)',
                      border: '1px solidrgb(1, 138, 8)',
                      borderRadius: '8px',
                      color: 'rgb(255, 255, 255)',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'inline-block',
                      margin: '8px 0'
                    }}>
                      {t("emergency.liveBroadcast")}
                    </div>
                  </>
                )}
                {!isUnauthorized && (
                  <div className="emergency-broadcast-caseid">
                    {isLoadingCaseData ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #E5E7EB',
                          borderTop: '2px solid #3B82F6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        {t("emergency.loadingDetails")}
                      </div>
                    ) : (
                      <div>{t("emergency.caseId")} {caseData?.case_ref || caseData?.case_ref}</div>
                    )}
                  </div>
                )}
              </div>
              {!isUnauthorized && (
                <>
                  <div className="emergency-broadcast-message-section">
                    <label className="emergency-broadcast-message-label">
                      {t("emergency.yourMessage")}
                    </label>
                    <div className="emergency-broadcast-message-box">
                      {caseData?.problem_description || localStorage.getItem("urgentDescription") || ""}
                    </div>
                  </div>

                  {/* Doctor and Clinic Information Section */}
                  {(caseData?.doctor || caseData?.clinic) && (
                    <div className="emergency-broadcast-care-team-section" style={{ marginTop: '20px' }}>
                      <label className="emergency-broadcast-message-label">
                        {t("emergency.careTeam")}
                      </label>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {/* Doctor Card */}
                        {caseData?.doctor && (
                          <div style={{
                            flex: '1',
                            minWidth: '280px',
                            padding: '20px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                          }}>
                            <div style={{ position: 'relative' }}>
                              <img
                                src={caseData.doctor.profile_image}
                                alt={`Dr. ${caseData.doctor.display_name ||
                                  caseData.doctor.full_name ||
                                  (caseData.doctor.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                                  caseData.doctor.name ||
                                  caseData.doctor.username ||
                                  t("emergency.veterinarian")
                                  }`}
                                style={{
                                  width: '70px',
                                  height: '70px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '3px solid #3B82F6'
                                }}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAiIGhlaWdodD0iNzAiIHZpZXdCb3g9IjAgMCA3MCA3MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMzUiIGN5PSIzNSIgcj0iMzUiIGZpbGw9IiNGM0Y0RjYiLz4KPHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSIyMCIgeT0iMjAiPgo8cGF0aCBkPSJNMTUgMTVMMjAgMjBMMTUgMjVMMTAgMjBMMTUgMTVaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo8L3N2Zz4K';
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#10B981',
                                borderRadius: '50%',
                                border: '2px solid #FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>MD</span>
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: 'bold',
                                color: '#1F2937',
                                fontSize: '18px',
                                marginBottom: '4px'
                              }}>
                                Dr. {
                                  caseData.doctor.display_name ||
                                  caseData.doctor.full_name ||
                                  (caseData.doctor.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                                  caseData.doctor.name ||
                                  caseData.doctor.username ||
                                  t("emergency.veterinarian")
                                }
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                marginBottom: '8px'
                              }}>
                                {t("emergency.roleVeterinarian")}
                              </div>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 8px',
                                backgroundColor: '#F0F9FF',
                                color: '#0369A1',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  backgroundColor: '#10B981',
                                  borderRadius: '50%'
                                }}></div>
                                {t("emergency.available")}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Clinic Card */}
                        {caseData?.clinic && (
                          <div style={{
                            flex: '1',
                            minWidth: '280px',
                            padding: '20px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px'
                          }}>
                            <div style={{ position: 'relative' }}>
                              <img
                                src={caseData.clinic.logo}
                                alt={caseData.clinic.name_en}
                                style={{
                                  width: '70px',
                                  height: '70px',
                                  borderRadius: '12px',
                                  objectFit: 'cover',
                                  border: '3px solid #8B5CF6'
                                }}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAiIGhlaWdodD0iNzAiIHZpZXdCb3g9IjAgMCA3MCA3MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjcwIiBoZWlnaHQ9IjcwIiByeD0iMTIiIGZpbGw9IiNGM0Y0RjYiLz4KPHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSIyMCIgeT0iMjAiPgo8cGF0aCBkPSJNMTUgMTBMMjAgMTVMMTUgMjBMMTAgMTVMMTUgMTBaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo8L3N2Zz4K';
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#8B5CF6',
                                borderRadius: '50%',
                                border: '2px solid #FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>🏥</span>
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: 'bold',
                                color: '#1F2937',
                                fontSize: '18px',
                                marginBottom: '4px'
                              }}>
                                {caseData.clinic.name_en}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                marginBottom: '8px'
                              }}>
                                {t("emergency.veterinaryClinic")}
                              </div>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 8px',
                                backgroundColor: '#F3E8FF',
                                color: '#7C3AED',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  backgroundColor: '#8B5CF6',
                                  borderRadius: '50%'
                                }}></div>
                                {t("emergency.partnerClinic")}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Pet Information Section */}
                  {caseData?.pets && caseData.pets.length > 0 && (
                    <div className="emergency-broadcast-pets-section" style={{ marginTop: '20px' }}>
                      <label className="emergency-broadcast-message-label">
                        {t("emergency.petInformation")}
                      </label>
                      <div className="emergency-broadcast-pets-box" style={{
                        padding: '15px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        backgroundColor: '#F9FAFB'
                      }}>
                        {caseData.pets.map((pet, index) => (
                          <div key={pet.id || index} style={{
                            marginBottom: '15px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px'
                          }}>
                            {pet.profile_image && (
                              <img
                                src={pet.profile_image}
                                alt={`${pet.name} profile`}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid #E5E7EB'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', color: '#374151', fontSize: '16px' }}>
                                {pet.name} ({typeof pet.species === "object" ? pet.species.name : pet.species})
                              </div>
                              <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
                                {typeof pet.breed === "object" ? pet.breed.name : pet.breed} • {pet.age} • {pet.gender ? t(`accountPreferences.profile.gender.${pet.gender.toLowerCase()}`, { defaultValue: pet.gender }) : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Media Section */}
                  {caseData?.media && caseData.media.length > 0 && (
                    <div className="emergency-broadcast-media-section" style={{ marginTop: '20px' }}>
                      <label className="emergency-broadcast-message-label">
                        {t("emergency.attachedMedia")}
                      </label>
                      <div className="emergency-broadcast-media-box" style={{
                        padding: '15px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        backgroundColor: '#F9FAFB'
                      }}>
                        {caseData.media.map((media, index) => (
                          <div key={index} style={{ marginBottom: '10px' }}>
                            {media.is_image ? (
                              <img
                                src={media.url}
                                alt={`Case media ${index + 1}`}
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '150px',
                                  borderRadius: '4px',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : media.is_video ? (
                              <video
                                src={media.url}
                                controls
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '150px',
                                  borderRadius: '4px'
                                }}
                              />
                            ) : (
                              <div style={{ color: '#6B7280' }}>
                                {t("emergency.mediaFile")} {media.url.split('/').pop()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="emergency-broadcast-actions">
                    {isTimerExpired ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '20px',
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #DC2626',
                        borderRadius: '8px'
                      }}>
                        <div style={{ color: '#DC2626', fontSize: '16px', fontWeight: '500' }}>
                          {t("emergency.windowExpired")}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          <button
                            onClick={() => navigate(getDashboardUrl())}
                            style={{
                              backgroundColor: '#6B7280',
                              color: 'white',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            {t("emergency.backToDashboard")}
                          </button>
                          <button
                            onClick={() => navigate(getUrgentCaseSubmitUrl(userId))}
                            style={{
                              backgroundColor: '#DC2626',
                              color: 'white',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: '6px',
                              fontSize: '16px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            {t("emergency.submitNewCase")}
                          </button>
                        </div>
                      </div>
                    ) : isCaseFinished ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '20px',
                        backgroundColor: caseData?.status === 'closed' ? '#FFFBEB' : '#F0FDF4',
                        border: `1px solid ${caseData?.status === 'closed' ? '#F59E0B' : '#10B981'}`,
                        borderRadius: '8px'
                      }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '500'
                        }}>
                          {caseData?.status === 'closed'
                            ? t("emergency.caseClosed")
                            : t("emergency.sosCompletedBy", {
                              name: caseData?.doctor?.display_name ||
                                caseData?.doctor?.full_name ||
                                (caseData?.doctor?.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                                caseData?.doctor?.name ||
                                caseData?.doctor?.username ||
                                t("emergency.veterinarian")
                            })
                          }
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          {caseData?.status === 'closed' ? (
                            <button
                              onClick={() => navigate(getDashboardUrl())}
                              style={{
                                backgroundColor: '#6B7280',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              {t("emergency.backToDashboard")}
                            </button>
                          ) : (
                            <>
                              <button
                                className="emergency-broadcast-end-btn"
                                onClick={() =>
                                  navigate(`/medical/dashboard/petguardian/urgent-case-submit/${userId}`)
                                }
                                style={{
                                  backgroundColor: '#10B981',
                                  color: 'white',
                                  border: 'none',
                                  padding: '12px 24px',
                                  borderRadius: '6px',
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                {t("emergency.viewCaseSummary")}
                              </button>
                              <button
                                onClick={() => navigate(getDashboardUrl())}
                                style={{
                                  backgroundColor: '#6B7280',
                                  color: 'white',
                                  border: 'none',
                                  padding: '12px 24px',
                                  borderRadius: '6px',
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                {t("emergency.backToDashboard")}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : isCaseAccepted ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '20px',
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #10B981',
                        borderRadius: '8px'
                      }}>
                         <div style={{ color: '#059669', fontSize: '16px', fontWeight: '500' }}>
                          {t("emergency.caseAcceptedBy", {
                            name: caseData?.doctor?.display_name ||
                              caseData?.doctor?.full_name ||
                              (caseData?.doctor?.first_name ? `${caseData.doctor.first_name} ${caseData.doctor.last_name || ''}`.trim() : null) ||
                              caseData?.doctor?.name ||
                              caseData?.doctor?.username ||
                              t("emergency.veterinarian")
                          })}
                        </div>
                        <button
                          className="emergency-broadcast-end-btn"
                          onClick={() =>
                            navigate(`/medical/dashboard/petguardian/urgent-case-submit/${userId}`)
                          }
                          style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          {t("emergency.viewCaseDetails")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          className="emergency-broadcast-extend-btn"
                          onClick={handleExtendExpiration}
                          disabled={isCaseAccepted || isCaseFinished || isExtending || Math.floor(secondsLeft / 60) >= 15}
                          style={{
                            opacity: (isCaseAccepted || isCaseFinished || isExtending || Math.floor(secondsLeft / 60) >= 15) ? 0.5 : 1,
                            cursor: (isCaseAccepted || isCaseFinished || isExtending || Math.floor(secondsLeft / 60) >= 15) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isExtending ? t("emergency.extending") : Math.floor(secondsLeft / 60) >= 15 ? t("emergency.extensionNotAvailable") : t("emergency.extendBroadcasting")}
                        </button>
                        <button
                          className="emergency-broadcast-end-btn"
                          disabled={isEnding}
                          style={{
                            backgroundColor: isEnding ? '#9CA3AF' : '#DC2626',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: isEnding ? 'not-allowed' : 'pointer',
                            opacity: isEnding ? 0.7 : 1
                          }}
                          onClick={handleEndCase}
                        >
                          {isEnding ? t("emergency.ending") : t("emergency.endRequest")}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* End Case Confirmation Modal */}
      {showEndCaseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isEnding) {
              cancelEndCase();
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'scale(1)',
              transition: 'transform 0.2s ease-out',
              fontFamily: '"Red Hat Display", sans-serif'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              {/* Warning Icon */}
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto'
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#1F2937',
                  margin: '0 0 8px 0'
                }}
              >
                {t("emergency.endBroadcastTitle")}
              </h3>

              {/* Message */}
              <p
                style={{
                  fontSize: '16px',
                  color: '#6B7280',
                  margin: 0,
                  lineHeight: '1.5'
                }}
              >
                {t("emergency.endBroadcastConfirm")}
              </p>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}
            >
              {/* Cancel Button */}
              <button
                onClick={cancelEndCase}
                disabled={isEnding}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: isEnding ? '#9CA3AF' : '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: isEnding ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Red Hat Display", sans-serif',
                  minWidth: '80px'
                }}
                onMouseEnter={(e) => {
                  if (!isEnding) {
                    e.target.style.backgroundColor = '#F9FAFB';
                    e.target.style.borderColor = '#9CA3AF';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isEnding) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
              >
                {t("emergency.cancel")}
              </button>

              {/* End Button */}
              <button
                onClick={confirmEndCase}
                disabled={isEnding}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isEnding ? '#FCA5A5' : '#EF4444',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: isEnding ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Red Hat Display", sans-serif',
                  minWidth: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isEnding) {
                    e.target.style.backgroundColor = '#DC2626';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isEnding) {
                    e.target.style.backgroundColor = '#EF4444';
                  }
                }}
              >
                {isEnding && (
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #FFFFFF',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                )}
                {isEnding ? t("emergency.ending") : t("emergency.endBroadcast")}
              </button>
            </div>
          </div>

          {/* Add keyframes for spinner animation */}
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
};

export default EmergencyBroadcast;